import {
  EventStatusColor,
  EventStatus,
  EventKind,
} from '@/core/constants/schedule/event';
import { EVENT_CONFIRMATION_GRACE_PERIOD_MINUTES } from '@/core/constants/schedule/notification';
import { getCustomerInfo } from '@/repositories/Customer';
import moment from 'moment';

export const fillEventsCustomerName = (events, customers) => {
  return events.map((event) => {
    if (!event.customer) return event;

    const customerName = customers.find(
      (customer) => String(customer.code) == String(event.customer.code)
    )?.name;

    return {
      ...event,
      customer: { ...event.customer, name: customerName },
    };
  });
};

export const enrichEventsWithCustomerNames = async (events, customers) => {
  const filledEvents = fillEventsCustomerName(events, customers);

  const missingCodes = [
    ...new Set(
      filledEvents
        .filter((e) => e.customer?.code && !e.customer?.name)
        .map((e) => e.customer.code)
    ),
  ];

  if (!missingCodes.length) return filledEvents;

  const missingCustomers = await Promise.all(
    missingCodes.map((code) => getCustomerInfo(code).catch(() => null))
  );

  const customerMap = new Map(
    missingCustomers.filter(Boolean).map((c) => [String(c.code), c])
  );

  return filledEvents.map((event) => {
    if (!event.customer?.code || event.customer?.name) return event;
    const customer = customerMap.get(String(event.customer.code));
    return customer ? { ...event, customer } : event;
  });
};

export const isWithinConfirmationWindow = (event) => {
  const now = moment();

  const startDate = moment(event.startDate);
  const eventConfirmationLimit = moment(event.confirmationDeadline);

  return now.isBetween(startDate, eventConfirmationLimit);
};

export const isConfirmationWindowExpired = (endDate) => {
  return moment(endDate)
    .add(EVENT_CONFIRMATION_GRACE_PERIOD_MINUTES, 'minutes')
    .isBefore(moment());
};

export const getEventStatusColor = (event, ignoreExecutionColor = false) => {
  const requiresTaskConfirmation = event.requiresAdditionalInformation;

  if (requiresTaskConfirmation) {
    return EventStatusColor.PENDING;
  }

  const status = event.planningClassification?.value;
  const hasExecution = !!event.execution;

  const colors = {
    [EventStatus.PLANNED]: EventStatusColor.PLANNED,
    [EventStatus.UNPLANNED]: EventStatusColor.UNPLANNED,
    [EventStatus.OVERDUE]: EventStatusColor.OVERDUE,
  };

  const color = colors[status] ?? EventStatusColor.DEFAULT;

  if (ignoreExecutionColor) return color;
  return hasExecution ? color : `${color}99`;
};

export const matchEvent = (event, target) =>
  (event.kind === EventKind.SINGLE ? event.id : event.occurrenceId) ===
  (target.kind === EventKind.SINGLE ? target.id : target.occurrenceId);
